import React from 'react';
import { Mic, Target, CheckCircle, FileText, MessageSquare, Brain, Sparkles } from 'lucide-react';

const DashboardTab = ({ stats, setActiveTab, user }) => {
    const firstName =
        user?.displayName?.trim()?.split(/\s+/)?.[0] ||
        (user?.email && user.email.split('@')[0]) ||
        null;

    const statCards = [
        {
            label: 'Recordings',
            value: stats.totalRecordings,
            icon: Mic,
            iconClass: 'text-blue-600',
            tileClass: 'from-blue-50 to-blue-100/80',
            tab: 'knowledgeCapture',
            hint: 'View capture & recordings'
        },
        {
            label: 'Pending tasks',
            value: stats.pendingTasks,
            icon: Target,
            iconClass: 'text-amber-600',
            tileClass: 'from-amber-50 to-orange-100/80',
            tab: 'tasks',
            hint: 'Open tasks'
        },
        {
            label: 'Completed',
            value: stats.completedTasks,
            icon: CheckCircle,
            iconClass: 'text-emerald-600',
            tileClass: 'from-emerald-50 to-green-100/80',
            tab: 'tasks',
            hint: 'Open tasks'
        },
        {
            label: 'Documents',
            value: stats.totalDocuments,
            icon: FileText,
            iconClass: 'text-violet-600',
            tileClass: 'from-violet-50 to-purple-100/80',
            tab: 'documents',
            hint: 'View documents'
        }
    ];

    const shortcuts = [
        { label: 'Capture', tab: 'knowledgeCapture', icon: Mic },
        { label: 'Knowledge search', tab: 'chat', icon: MessageSquare },
        { label: 'Documents', tab: 'documents', icon: FileText },
        { label: 'Brain', tab: 'brain', icon: Brain }
    ];

    return (
        <div className="space-y-8 w-full max-w-none">
            <div className="rounded-2xl border border-gray-200/80 bg-gradient-to-br from-white via-white to-slate-50/90 backdrop-blur-xl p-6 md:p-8 shadow-sm">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                    <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-500 flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-amber-500 shrink-0" aria-hidden />
                            Workspace overview
                        </p>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1 tracking-tight">
                            {firstName ? `Hi, ${firstName}` : 'Welcome'}
                        </h1>
                        <p className="text-sm text-slate-600 mt-2 max-w-xl leading-relaxed">
                            Your dashboard summarizes recordings, tasks, and documents. Use the shortcuts to jump
                            straight into common actions.
                        </p>
                    </div>
                    {stats.tasksDonePercent != null && stats.totalTasks > 0 && (
                        <div className="rounded-xl bg-white/90 border border-slate-200/80 px-5 py-4 shadow-sm shrink-0">
                            <p className="text-3xl font-bold text-slate-900 tabular-nums">
                                {stats.tasksDonePercent}%
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5">Tasks completed</p>
                            <p className="text-xs text-slate-400 mt-1">
                                {stats.completedTasks} of {stats.totalTasks}
                            </p>
                        </div>
                    )}
                </div>
                <div className="flex flex-wrap gap-2 mt-6 pt-6 border-t border-slate-200/60">
                    {shortcuts.map(({ label, tab, icon: Icon }) => (
                        <button
                            key={tab}
                            type="button"
                            onClick={() => setActiveTab(tab)}
                            className="inline-flex items-center gap-2 rounded-xl border border-slate-200/80 bg-white/90 px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 hover:border-slate-300 transition-colors"
                        >
                            <Icon className="w-4 h-4 text-slate-500" strokeWidth={2} aria-hidden />
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            <div>
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">At a glance</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 w-full">
                    {statCards.map((stat) => (
                        <div key={`${stat.label}-${stat.tab}`} className="relative group">
                            <button
                                type="button"
                                onClick={() => setActiveTab(stat.tab)}
                                className="w-full text-left bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-gray-200/60 shadow-sm hover:shadow-md hover:border-slate-300/80 transition-all duration-200"
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div
                                        className={`w-12 h-12 bg-gradient-to-br ${stat.tileClass} rounded-xl flex items-center justify-center border border-white/60 shadow-inner`}
                                    >
                                        <stat.icon
                                            className={`w-6 h-6 ${stat.iconClass}`}
                                            strokeWidth={2}
                                            aria-hidden
                                        />
                                    </div>
                                    <div className="text-right min-w-0 flex-1">
                                        <p className="text-3xl font-bold text-slate-900 tabular-nums leading-none">
                                            {stat.value}
                                        </p>
                                        <p className="text-sm text-slate-600 mt-2 font-medium">{stat.label}</p>
                                    </div>
                                </div>
                                <p className="text-xs text-slate-400 mt-4 group-hover:text-slate-500 transition-colors">
                                    {stat.hint}
                                    <span aria-hidden> →</span>
                                </p>
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default DashboardTab;
